import { useMemo } from "react";

export type GNode = { id: string; type: string; label: string; created_at?: string | null };
export type GEdge = { src: string; rel: string; dst: string };
export type Sub = { nodes: GNode[]; edges: GEdge[] };

export type Shape = "circle" | "square" | "diamond" | "triangle";

// Node type → colour + shape + plain-English meaning, grouped by the firm's
// spine. Shared vocabulary: the standalone explorer and (later) the
// advisor's "highlight this" tool both render through this same map, so a
// node always means the same thing wherever it's drawn. Shape carries real
// meaning of its own, not just colour — trades (Order/Fill) and the trade
// idea that preceded them (Proposal) need to be spottable at a glance in a
// dense graph, which several same-family colours (the purple board-cluster,
// the pink review-cluster) can't do alone (found 2026-08-04, Alan: "not
// easy to find trades in the graph... different style node not just color").
// Everything else stays a circle — the default, no meaning attached.
export const TYPE: Record<string, { c: string; what: string; shape?: Shape }> = {
  ConstitutionVersion: { c: "#cbd5e1", what: "the charter" },
  LongTermThesis: { c: "#e8b339", what: "growth thesis" },
  BoardSession: { c: "#7c5cff", what: "a board meeting" },
  MacroBrief: { c: "#38bdf8", what: "macro/news evidence" },
  SectorDigest: { c: "#38bdf8", what: "sector-desk evidence" },
  BoardMember: { c: "#a78bfa", what: "a board seat" },
  Perspective: { c: "#a78bfa", what: "a member's view" },
  Synthesis: { c: "#a78bfa", what: "the chair's synthesis" },
  RiskReview: { c: "#a78bfa", what: "the risk officer's review" },
  Directive: { c: "#33e07a", what: "an order to the desk" },
  TraderAgent: { c: "#d9a441", what: "Stonky / a trader" },
  SeniorRuling: { c: "#d9a441", what: "Stonky's ruling" },
  Proposal: { c: "#3aa0ff", what: "a trade idea", shape: "triangle" },
  Order: { c: "#2ec4b6", what: "an order sent", shape: "square" },
  Fill: { c: "#2ec4b6", what: "a fill", shape: "diamond" },
  Trigger: { c: "#ff3b4d", what: "an alert" },
  PostMortem: { c: "#ff6ac1", what: "a review" },
  TrendInsight: { c: "#ff6ac1", what: "a trend" },
  LongTermHolding: { c: "#e8b339", what: "a held position" },
  Conversion: { c: "#ff9f43", what: "a rescue" },
};
export const colorOf = (t: string) => TYPE[t]?.c ?? "#8b93a7";
export const shapeOf = (t: string): Shape => TYPE[t]?.shape ?? "circle";

/** One shape mark, sized by radius `r`, for both the canvas nodes and every
 * small legend/list swatch elsewhere — one place decides what a type looks
 * like, not five copies of a colour dot. `x`/`y` let it drop into an
 * existing <g transform> (canvas) or sit at the SVG's own origin (a
 * standalone icon-sized swatch). */
export function ShapeMark({ type, r, x = 0, y = 0, fill, stroke, strokeWidth = 0 }: {
  type: string; r: number; x?: number; y?: number; fill: string; stroke?: string; strokeWidth?: number;
}) {
  const shape = shapeOf(type);
  const common = { fill, stroke: stroke ?? "transparent", strokeWidth };
  if (shape === "square") {
    const s = r * 1.7;
    return <rect x={x - s / 2} y={y - s / 2} width={s} height={s} rx={s * 0.18} {...common} />;
  }
  if (shape === "diamond") {
    const d = r * 1.15;
    return <polygon points={`${x},${y - d} ${x + d},${y} ${x},${y + d} ${x - d},${y}`} {...common} />;
  }
  if (shape === "triangle") {
    const t = r * 1.3;
    const h = t * 0.87;
    return <polygon points={`${x},${y - t} ${x + h},${y + t * 0.5} ${x - h},${y + t * 0.5}`} {...common} />;
  }
  return <circle cx={x} cy={y} r={r} {...common} />;
}

/** A small, non-canvas swatch for legends/lists — a plain span won't draw a
 * triangle or diamond, so this is a tiny standalone SVG of the same mark. */
export function ShapeSwatch({ type, size = 10 }: { type: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox={`${-size / 2} ${-size / 2} ${size} ${size}`} className="flex-shrink-0">
      <ShapeMark type={type} r={size * 0.4} fill={colorOf(type)} />
    </svg>
  );
}

// ── Lanes: the firm's spine, as a rank order ────────────────────────────────
// The ontology is a pipeline — charter → board → desk → market → learning —
// not an undirected network. A force simulation throws that structure away
// and rediscovers a blob; a lane layout states it. y = lane, x = time.
export const LANES: { key: string; label: string; hint: string }[] = [
  { key: "governance", label: "GOVERNANCE", hint: "charter, board, directives" },
  { key: "desk", label: "DESK", hint: "trade ideas and rulings" },
  { key: "market", label: "MARKET", hint: "orders and fills" },
  { key: "learning", label: "LEARNING", hint: "post-mortems and trends" },
  { key: "evidence", label: "EVIDENCE", hint: "macro and sector reads" },
];
export const LANE_OF: Record<string, number> = {
  ConstitutionVersion: 0, BoardSession: 0, BoardMember: 0, Perspective: 0,
  Synthesis: 0, RiskReview: 0, Directive: 0, Trigger: 0,
  TraderAgent: 1, Proposal: 1, SeniorRuling: 1,
  Order: 2, Fill: 2, LongTermHolding: 2, Conversion: 2,
  PostMortem: 3, TrendInsight: 3,
  MacroBrief: 4, SectorDigest: 4, LongTermThesis: 4,
};
export const laneOf = (t: string) => LANE_OF[t] ?? 0;

const W = 760;
const H = 480;

/** Deterministic lane layout: y = ontology lane, x = day column, and within a
 *  cell nodes stack in a stable order. Same data always draws the same
 *  picture, so filtering a type REMOVES marks instead of teleporting every
 *  survivor — the force sim re-seeded with Math.random() on every filter
 *  toggle, which destroyed the user's mental map on the interaction most
 *  likely to be repeated. It is also O(n) rather than O(n²)×320 iterations. */
export function laneLayout(nodes: GNode[], dayKeys: string[]) {
  const pos = new Map<string, { x: number; y: number }>();
  const padL = 74, padR = 16, padT = 18, padB = 20;
  const cols = Math.max(1, dayKeys.length);
  const colW = (W - padL - padR) / cols;
  const laneH = (H - padT - padB) / LANES.length;
  const colIndex = new Map(dayKeys.map((d, i) => [d, i]));
  // Stable ordering inside a cell: oldest first, then by id so ties never
  // depend on fetch order.
  const sorted = [...nodes].sort((a, b) =>
    (a.created_at ?? "").localeCompare(b.created_at ?? "") || a.id.localeCompare(b.id));
  const cell = new Map<string, number>();
  for (const n of sorted) {
    const lane = laneOf(n.type);
    const day = dayOf(n);
    const ci = colIndex.get(day) ?? cols - 1;
    const k = `${lane}:${ci}`;
    const seq = cell.get(k) ?? 0;
    cell.set(k, seq + 1);
    // Fan within the cell: across first, then wrap into rows, so a busy
    // morning stays inside its own day column instead of bleeding sideways.
    const perRow = Math.max(1, Math.floor(colW / 17));
    const gx = seq % perRow, gy = Math.floor(seq / perRow);
    pos.set(n.id, {
      x: padL + ci * colW + 12 + gx * 16,
      y: padT + lane * laneH + 16 + (gy % Math.max(1, Math.floor(laneH / 15))) * 14,
    });
  }
  return pos;
}

/** Local calendar day of a node, as YYYY-MM-DD. Nodes with no timestamp fall
 *  into the newest column rather than vanishing. */
export function dayOf(n: GNode): string {
  if (!n.created_at) return "";
  const d = new Date(n.created_at);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function layout(nodes: GNode[], edges: GEdge[]) {
  const pos = new Map(nodes.map((n, i) => ({
    id: n.id,
    p: { x: W / 2 + Math.cos(i * 2.4) * 140 + (Math.random() - 0.5) * 30,
         y: H / 2 + Math.sin(i * 2.4) * 120 + (Math.random() - 0.5) * 30, vx: 0, vy: 0 },
  })).map(({ id, p }) => [id, p]));
  const links = edges.filter((e) => pos.has(e.src) && pos.has(e.dst));
  for (let it = 0; it < 320; it++) {
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const a = pos.get(nodes[i].id)!, b = pos.get(nodes[j].id)!;
        const dx = a.x - b.x, dy = a.y - b.y, d2 = dx * dx + dy * dy + 0.01, d = Math.sqrt(d2);
        const f = 2600 / d2, fx = (dx / d) * f, fy = (dy / d) * f;
        a.vx += fx; a.vy += fy; b.vx -= fx; b.vy -= fy;
      }
    }
    for (const e of links) {
      const a = pos.get(e.src)!, b = pos.get(e.dst)!;
      const dx = b.x - a.x, dy = b.y - a.y, d = Math.sqrt(dx * dx + dy * dy) + 0.01, f = (d - 95) * 0.025;
      const fx = (dx / d) * f, fy = (dy / d) * f;
      a.vx += fx; a.vy += fy; b.vx -= fx; b.vy -= fy;
    }
    for (const n of nodes) {
      const p = pos.get(n.id)!;
      p.vx += (W / 2 - p.x) * 0.002; p.vy += (H / 2 - p.y) * 0.002;
      p.x += p.vx * 0.82; p.y += p.vy * 0.82; p.vx *= 0.82; p.vy *= 0.82;
      p.x = Math.max(24, Math.min(W - 24, p.x)); p.y = Math.max(24, Math.min(H - 24, p.y));
    }
  }
  return pos;
}

export type GraphCanvasProps = {
  sub: Sub;
  hover: string | null;
  onHover: (id: string | null) => void;
  selected: string | null;
  onSelect: (id: string) => void;
  /** When set (non-empty), nodes/edges outside it are dimmed regardless of
   * hover — a trace-path result or (later) the advisor's highlight tool.
   * Undefined/empty falls back to plain hover-based neighbour dimming. */
  highlightIds?: Set<string>;
  /** Edges to draw as the active path (thicker, gold) — pass the trace-path
   * result's edges as `${src}>${rel}>${dst}` keys. */
  pathEdgeKeys?: Set<string>;
  /** "lane" (default) puts ontology on y and time on x — deterministic, and
   * the right shape for a pipeline. "force" keeps the old simulation for a
   * handful of nodes with no meaningful time spread (a traced chain, an
   * advisor highlight), where a day axis would be one column of dots. */
  layoutMode?: "lane" | "force";
};

/** The force-directed SVG renderer, extracted so the standalone Graph
 * Explorer and any future embedded view (a chat "highlight this" result, a
 * position's own decision chain) share one rendering path instead of two
 * graphs drifting apart. */
export default function GraphCanvas({
  sub, hover, onHover, selected, onSelect, highlightIds, pathEdgeKeys, layoutMode = "lane",
}: GraphCanvasProps) {
  // The day columns present in this slice, oldest → newest. Undated nodes
  // share one trailing column rather than being dropped.
  const dayKeys = useMemo(() => {
    const s = new Set(sub.nodes.map(dayOf));
    return [...s].sort((a, b) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b)));
  }, [sub]);
  const pos = useMemo(() => {
    if (!sub.nodes.length) return null;
    return layoutMode === "lane" ? laneLayout(sub.nodes, dayKeys) : layout(sub.nodes, sub.edges);
  }, [sub, layoutMode, dayKeys]);
  const nbrs = useMemo(() => {
    const m = new Set<string>();
    if (hover) for (const e of sub.edges) {
      if (e.src === hover) m.add(e.dst);
      if (e.dst === hover) m.add(e.src);
    }
    return m;
  }, [hover, sub]);

  const hasHighlight = !!highlightIds && highlightIds.size > 0;
  const dimNode = (id: string) =>
    hasHighlight ? !highlightIds!.has(id) : hover !== null && id !== hover && !nbrs.has(id);
  const edgeKey = (e: GEdge) => `${e.src}>${e.rel}>${e.dst}`;
  const dimEdge = (e: GEdge) =>
    hasHighlight ? !(highlightIds!.has(e.src) && highlightIds!.has(e.dst))
                 : hover !== null && e.src !== hover && e.dst !== hover;

  if (!pos || sub.nodes.length === 0) {
    return (
      <div className="p-8 text-slate-500 text-sm text-center">
        Nothing to show — the first board session draws the first nodes.
      </div>
    );
  }

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 520 }}
         onMouseLeave={() => onHover(null)}>
      <defs>
        <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M0,0 L10,5 L0,10 z" fill="#334155" />
        </marker>
      </defs>
      {/* Lane bands + day columns: the grammar of the picture, drawn once.
          Down = caused, right = later. A reader learns it once and it holds
          on every graph in the app. */}
      {layoutMode === "lane" && (
        <g>
          {LANES.map((ln, i) => {
            const laneH = (H - 38) / LANES.length;
            const y = 18 + i * laneH;
            return (
              <g key={ln.key}>
                {i % 2 === 1 && <rect x={0} y={y} width={W} height={laneH} fill="#0f172a" opacity={0.45} />}
                <text x={6} y={y + 13} fontSize="9" fill="#64748b" className="font-mono">{ln.label}</text>
              </g>
            );
          })}
          {dayKeys.map((d, i) => {
            const colW = (W - 90) / Math.max(1, dayKeys.length);
            const x = 74 + i * colW;
            const label = d
              ? new Date(d + "T12:00:00").toLocaleDateString([], { weekday: "short", day: "numeric" })
              : "undated";
            return (
              <g key={d || "undated"}>
                {i > 0 && <line x1={x} y1={14} x2={x} y2={H - 18} stroke="#1e293b" strokeWidth={1} />}
                <text x={x + 4} y={11} fontSize="9" fill="#64748b" className="font-mono">{label}</text>
              </g>
            );
          })}
        </g>
      )}
      {sub.edges.map((e, i) => {
        const a = pos.get(e.src), b = pos.get(e.dst);
        if (!a || !b) return null;
        const onPath = pathEdgeKeys?.has(edgeKey(e)) ?? false;
        const active = (hover !== null && (e.src === hover || e.dst === hover)) || onPath;
        return (
          <g key={i} opacity={dimEdge(e) && !onPath ? 0.12 : 0.5}>
            <line x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                  stroke={onPath ? "#d9a441" : active ? "#d9a441" : "#334155"}
                  strokeWidth={onPath ? 2.4 : active ? 1.6 : 1}
                  markerEnd="url(#arrow)" />
            {active && (
              <text x={(a.x + b.x) / 2} y={(a.y + b.y) / 2 - 3} fontSize="9"
                    fill="#d9a441" textAnchor="middle" className="font-mono">{e.rel}</text>
            )}
          </g>
        );
      })}
      {sub.nodes.map((n) => {
        const p = pos.get(n.id)!;
        const isH = n.id === hover || nbrs.has(n.id) || (hasHighlight && highlightIds!.has(n.id));
        return (
          <g key={n.id} transform={`translate(${p.x},${p.y})`} opacity={dimNode(n.id) ? 0.25 : 1}
             onMouseEnter={() => onHover(n.id)} onClick={() => onSelect(n.id)}
             style={{ cursor: "pointer" }}>
            {/* An invisible fat hit target. The visible mark is r=6 (~12px),
                well under the ~44px a finger needs, and there is no hover on
                a touch screen to compensate. */}
            <circle r={14} fill="transparent" />
            <title>{`${n.label} — ${n.type}`}</title>
            {n.id === selected && (
              <ShapeMark type={n.type} r={12} fill="none" stroke="#d9a441" strokeWidth={1.5} />
            )}
            <ShapeMark type={n.type} r={isH ? 8 : 6} fill={colorOf(n.type)}
                       stroke={n.id === hover ? "#fff" : "transparent"} strokeWidth={1.5} />
            {/* Labels are hover-only in lane mode: a lane row is 14px tall and
                packed, so always-on text would overlap into mush. The <title>
                above keeps the full label reachable without hover, which is
                what a touch device and a screen reader actually use. */}
            {isH && (
              <text x={10} y={4} fontSize="10" fill="#cbd5e1" className="font-mono"
                    style={{ paintOrder: "stroke", stroke: "#0b1220", strokeWidth: 3 }}>
                {n.label.slice(0, 22)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
